import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Ban, Trash2, Gauge, Mail, Plus } from "lucide-react";

export default function Compliance() {
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const [reason, setReason] = useState("");
  const { data: suppression = [] } = useQuery({ queryKey: ["suppression"], queryFn: async () => (await api.get("/suppression")).data });
  const { data: opps = [] } = useQuery({ queryKey: ["compliance-opps"], queryFn: async () => (await api.get("/opportunities")).data });
  const { data: activity = [] } = useQuery({ queryKey: ["compliance-activity"], queryFn: async () => (await api.get("/activity?limit=200")).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["suppression"] });

  const add = async () => {
    if (!domain) return;
    try { await api.post("/suppression", { domain, reason: reason || "manual" }); toast.success("Domain suppressed"); setDomain(""); setReason(""); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sentCount = activity.filter((a) => a.action === "outreach_sent").length;
  const RATE_LIMIT = 10;
  const ratePct = Math.min(100, (sentCount / RATE_LIMIT) * 100);
  const contactHistory = activity.filter((a) => ["outreach_sent", "submission_completed"].includes(a.action));

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="compliance-view">
      <header>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Compliance Shield</h1>
        <p className="text-sm text-muted-foreground mt-1">Suppression, contact caps, rate limits and outreach provenance.</p>
      </header>

      {/* Guardrail surfaces */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-4 border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><Gauge className="h-4 w-4" /> Weekly outreach velocity</div>
          <div className="font-mono font-bold text-2xl">{sentCount}<span className="text-sm text-muted-foreground">/{RATE_LIMIT}</span></div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-2"><div className={`h-full rounded-full ${ratePct >= 80 ? "bg-rose-500" : ratePct >= 50 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${ratePct}%` }} /></div>
          {ratePct >= 80 && <p className="text-xs text-rose-400 mt-1.5">Approaching conservative rate limit.</p>}
        </Card>
        <Card className="p-4 border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><Ban className="h-4 w-4" /> Suppressed domains</div>
          <div className="font-mono font-bold text-2xl">{suppression.length}</div>
          <p className="text-xs text-muted-foreground mt-1.5">Blocked from all outreach.</p>
        </Card>
        <Card className="p-4 border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><Mail className="h-4 w-4" /> Contact cap</div>
          <div className="font-mono font-bold text-2xl">1<span className="text-sm text-muted-foreground"> / org</span></div>
          <p className="text-xs text-muted-foreground mt-1.5">One primary contact per organization.</p>
        </Card>
      </div>

      {/* Suppression manager */}
      <Card className="p-5 border-border">
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Suppression list</h2>
        <div className="flex items-end gap-3 mb-4 flex-wrap">
          <div className="flex-1 min-w-[180px]"><label className="text-xs text-muted-foreground">Domain</label><Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.org" data-testid="suppression-add-domain-input" className="mt-1" /></div>
          <div className="flex-1 min-w-[180px]"><label className="text-xs text-muted-foreground">Reason</label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="opted out / do not contact" className="mt-1" /></div>
          <Button onClick={add} data-testid="suppression-add-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Suppress</Button>
        </div>
        <div className="space-y-2">
          {suppression.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`suppression-${s.id}`}>
              <div><span className="font-mono text-sm">{s.domain}</span><span className="text-xs text-muted-foreground ml-2">{s.reason}</span></div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={async () => { await api.delete(`/suppression/${s.id}`); toast.success("Removed"); refresh(); }} data-testid={`remove-suppression-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          {suppression.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No suppressed domains.</p>}
        </div>
      </Card>

      {/* Contact history / provenance */}
      <Card className="p-5 border-border">
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Mail className="h-4 w-4" /> Contact history & provenance</h2>
        <div className="space-y-2">
          {contactHistory.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg border border-border p-3 text-sm" data-testid={`contact-log-${a.id}`}>
              <Badge variant="outline" className={a.action === "outreach_sent" ? "border-primary/30 bg-primary/10 text-primary" : "border-teal-500/30 bg-teal-500/10 text-teal-400"}>{a.action.replace(/_/g, " ")}</Badge>
              <span className="flex-1 text-muted-foreground">{a.detail}</span>
              <span className="text-xs text-muted-foreground/70">{fmtDateTime(a.timestamp)}</span>
            </div>
          ))}
          {contactHistory.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No contact events logged yet.</p>}
        </div>
      </Card>
    </div>
  );
}
